// IMEXHS "Medidas" tools built from straight segments, on the beta's BaseAnnotationTool. The geometry
// (handle layout, drag constraints, formulas) lives in geometry.ts and follows
// docs/measurement-constructions.md; this file only handles interaction and drawing.
//
// Constraints are applied at render time: each render compares the handles with the previous render,
// and when exactly one handle moved (a handle drag, or the new handle while drawing) the construction
// re-lays out or constrains the rest. A whole-measurement move changes every handle and is left as is.
import { currentMoveDistance } from './proximity';
import {
  type AnnotationToolClass,
  baseAnnotationTool,
  csCore,
  csTools,
  type Handle,
  type InteractionType,
  type MeasurementData,
  type MouseDetail,
  type Point,
  type Readout,
  type RenderDetail,
} from './cstypes';
import {
  canvasContext,
  canvasDistanceToSegment,
  drawLinkedTextBox,
  drawTextBox,
  formatAngle,
  formatLength,
  newTextBoxHandle,
  pointInBox,
  spacingOf,
  strokeLine,
} from './draw';
import {
  applyHandleChange,
  BIDIRECTIONAL,
  CARDIOTHORACIC_INDEX,
  type Construction,
  COXOMETRY,
  coxometryLabelPoints,
  type HandleMap,
  measureBidirectional,
  measureCardiothoracic,
  measureCoxometry,
  measureInsallSalvati,
  measureParallelDistance,
  measureTwoLineAngle,
  midpoint,
  PARALLEL_DISTANCE,
  parallelFeet,
  round,
  type Spacing,
  snapshotHandles,
  TWO_LINES,
} from './geometry';
import {
  findPendingAnywhere,
  isCrossImageOwner,
  isCrossProxy,
  keepPairConsistent,
  linkCrossImage,
  proxyOwner,
  updateAllViewports,
} from './crossview';

/** Dash of reference axes (parallel distance, coxometry), as IMEXHS draws them. */
const AXIS_DASH = [20, 2];
/** Canvas offset (px) of a segment's letter label from the segment midpoint. */
const LABEL_OFFSET_PX = 12;
/** Image-pixel distance of the CTI letters from their bars (A/B above, C below), as IMEXHS draws them. */
const CTI_LABEL_SHIFT_PX = 20;

/** Decimals IMEXHS shows and stores per value kind. */
export const DECIMALS = { length: 2, bidirectional: 1, cardiothoracicLength: 3, index: 2, ratio: 4, angle: 2 } as const;

export interface Measurement {
  /** Lines shown on the image next to the measurement. */
  canvas: string[];
  /** Lines shown on the workspace card. */
  readout: Readout[];
}

interface Segment {
  from: string;
  to: string;
  dashed?: boolean;
  /** Letter drawn next to the segment ("A", "B", "C"). */
  label?: string;
  /** Image-pixel shift of the letter from the segment midpoint (IMEXHS places CTI letters 20 px off). */
  labelShift?: Point;
}

interface Label {
  text: string;
  at: Point;
}

export interface SegmentToolSpec {
  name: string;
  construction: Construction;
  segments: Segment[];
  /** Free-standing labels (coxometry H1/H2/D1/D2/D/I). */
  labels?(h: HandleMap): Label[];
  measure(h: HandleMap, spacing: Spacing): Measurement;
  /** Line 2 may be drawn on another viewport's image (IMEXHS multi-view anteversion, crossview.ts). */
  crossViewport?: boolean;
  /** Anchor points for the text box link. */
  anchors?(h: HandleMap): Point[];
}

/** Last rendered handle positions per measurement, to see which handle moved since. */
const lastRendered = new WeakMap<MeasurementData, HandleMap>();

function newPoint(at: Point, active: boolean): Handle {
  return { x: at.x, y: at.y, highlight: true, active };
}

function segmentsDrawn(data: MeasurementData): number {
  return typeof data.segmentsDrawn === 'number' ? data.segmentsDrawn : 1;
}

/** Handles the user places by dragging (the rest are derived); only these must start inside the image. */
function drawnKeys(n: number): string[] {
  return n === 1 ? ['start', 'end'] : [`start${n}`, `end${n}`];
}

function handleMap(data: MeasurementData): HandleMap {
  return data.handles as HandleMap;
}

function hasHandles(data: MeasurementData, keys: string[]): boolean {
  return keys.every((key) => data.handles[key] !== undefined);
}

/** Builds a BaseAnnotationTool subclass from a spec. */
export function createSegmentTool(spec: SegmentToolSpec): AnnotationToolClass {
  const Base = baseAnnotationTool();
  const { construction } = spec;
  return class SegmentTool extends Base {
    constructor(name: string = spec.name) {
      super({ name, supportedInteractionTypes: ['mouse', 'touch'], configuration: { drawHandlesOnHover: false } });
    }

    createNewMeasurement(detail: MouseDetail): MeasurementData {
      const at = detail.currentPoints.image;
      const firstSegment = construction.segments === 1 ? construction.order : drawnKeys(1);
      const handles: Record<string, Handle> = {};
      for (const key of firstSegment) handles[key] = newPoint(at, key === 'end');
      handles.textBox = newTextBoxHandle();
      return { visible: true, active: true, complete: construction.segments === 1, segmentsDrawn: 1, locked: true, handles };
    }

    /** First drag creates the measurement; a second drag on the same image adds line 2 (two-line tools). */
    addNewMeasurement(evt: CustomEvent<MouseDetail>, interactionType: InteractionType): void {
      evt.preventDefault();
      evt.stopPropagation();
      const detail = evt.detail;
      const { element } = detail;
      const tools = csTools();
      const cross = spec.crossViewport ? findPendingAnywhere(this.name) : undefined;
      if (cross && cross.imageId !== detail.image.imageId) {
        this.addLineOnAnotherImage(evt, interactionType, cross.data, cross.imageId);
        return;
      }
      let data = this.pendingMeasurement(element);
      let segment = 1;
      if (data) {
        segment = segmentsDrawn(data) + 1;
        const [a, b] = drawnKeys(segment);
        data.handles[a] = newPoint(detail.currentPoints.image, false);
        data.handles[b] = newPoint(detail.currentPoints.image, true);
        data.segmentsDrawn = segment;
        data.active = true;
      } else {
        data = this.createNewMeasurement(detail);
        tools.addToolState(element, this.name, data);
        if (spec.crossViewport) watchModifications(element);
      }
      const measurement = data;
      const moving = measurement.handles[drawnKeys(segment)[1]];
      const mover = interactionType === 'mouse' ? tools.moveNewHandle : tools.moveNewHandleTouch;
      csCore().updateImage(element);
      mover(detail, this.name, measurement, moving, () => {
        measurement.active = false;
        moving.active = false;
        if (segmentsDrawn(measurement) >= construction.segments) measurement.complete = true;
        const placed = drawnKeys(segment).map((key) => measurement.handles[key]);
        if (placed.some((h) => h.x < 0 || h.y < 0 || h.x > detail.image.columns || h.y > detail.image.rows)) {
          tools.removeToolState(element, this.name, measurement);
        }
        csCore().updateImage(element);
      });
    }

    /**
     * Line 2 of a cross-viewport measurement on a different image: the owner gets the handles, this
     * image gets a proxy sharing them (crossview.ts), and every viewport redraws when either moves.
     */
    private addLineOnAnotherImage(
      evt: CustomEvent<MouseDetail>, interactionType: InteractionType, owner: MeasurementData, ownerImageId: string,
    ): void {
      const detail = evt.detail;
      const { element } = detail;
      const tools = csTools();
      owner.handles.start2 = newPoint(detail.currentPoints.image, false);
      owner.handles.end2 = newPoint(detail.currentPoints.image, true);
      owner.segmentsDrawn = 2;
      const proxy = linkCrossImage(owner, ownerImageId, detail.image.imageId, newTextBoxHandle());
      tools.addToolState(element, this.name, proxy);
      watchModifications(element);
      const moving = proxy.handles.end;
      const mover = interactionType === 'mouse' ? tools.moveNewHandle : tools.moveNewHandleTouch;
      updateAllViewports();
      mover(detail, this.name, proxy, moving, () => {
        owner.complete = true;
        owner.active = false;
        proxy.active = false;
        moving.active = false;
        const placed = [proxy.handles.start, proxy.handles.end];
        if (placed.some((h) => h.x < 0 || h.y < 0 || h.x > detail.image.columns || h.y > detail.image.rows)) {
          tools.removeToolState(element, this.name, proxy);
        }
        updateAllViewports();
      });
    }

    /** While a two-line measurement waits for line 2, a press goes to it even on top of line 1. */
    preMouseDownCallback(evt: CustomEvent<MouseDetail>): boolean {
      if (!this.pendingMeasurement(evt.detail.element)) return false;
      this.addNewMeasurement(evt, 'mouse');
      return true;
    }

    private pendingMeasurement(element: HTMLElement): MeasurementData | undefined {
      if (spec.crossViewport) return findPendingAnywhere(this.name)?.data;
      const toolData = csTools().getToolState(element, this.name)?.data as MeasurementData[] | undefined;
      return toolData?.find((data) => data.complete === false);
    }

    pointNearTool(element: HTMLElement, data: MeasurementData, coords: Point): boolean {
      if (data.visible === false || data.readOnly === true) return false;
      return this.distanceFromPoint(element, data, coords) < currentMoveDistance() || pointInBox(data.handles.textBox?.boundingBox, coords);
    }

    distanceFromPoint(element: HTMLElement, data: MeasurementData, coords: Point): number {
      let best = Number.POSITIVE_INFINITY;
      for (const s of visibleSegments(spec, data)) best = Math.min(best, canvasDistanceToSegment(element, data.handles[s.from], data.handles[s.to], coords));
      return best;
    }

    renderToolData(evt: CustomEvent<RenderDetail>): void {
      const { element, image } = evt.detail;
      const toolData = csTools().getToolState(element, this.name)?.data as MeasurementData[] | undefined;
      if (!toolData) return;
      const ctx = canvasContext(evt.detail.canvasContext);
      const spacing = spacingOf(image);
      for (const data of [...toolData]) {
        if (data.visible === false) continue;
        if (spec.crossViewport && !keepPairConsistent(this.name, data, image.imageId)) continue;
        if (isCrossProxy(data)) {
          renderProxy(spec, ctx, element, data, spacing);
          continue;
        }
        syncConstruction(construction, data, spacing);
        const color = csTools().toolColors.getColorIfActive(data);
        for (const s of visibleSegments(spec, data)) {
          strokeLine(ctx, element, data.handles[s.from], data.handles[s.to], { color, dash: s.dashed ? AXIS_DASH : undefined });
          if (s.label) {
            const middle = midpoint(data.handles[s.from], data.handles[s.to]);
            drawLabel(ctx, element, s.label, { x: middle.x + (s.labelShift?.x ?? 0), y: middle.y + (s.labelShift?.y ?? 0) }, color);
          }
        }
        const complete = data.complete !== false;
        if (complete) for (const label of spec.labels?.(handleMap(data)) ?? []) drawLabel(ctx, element, label.text, label.at, color);
        csTools().drawHandles(ctx, { element }, data.handles, color, { drawHandlesIfActive: false });
        if (!complete) {
          data.readout = [];
          continue;
        }
        const result = spec.measure(handleMap(data), spacing);
        data.readout = result.readout;
        if (result.canvas.length === 0) continue;
        const textBox = data.handles.textBox;
        if (!textBox.hasMoved) placeTextBox(textBox, data);
        drawLinkedTextBox(ctx, element, textBox, result.canvas, spec.anchors?.(handleMap(data)) ?? allHandlePoints(data), color);
      }
    }
  };
}

/** Re-lays out or constrains the measurement when exactly one handle moved since the last render. */
function syncConstruction(construction: Construction, data: MeasurementData, spacing: Spacing): void {
  const handles = handleMap(data);
  const previous = lastRendered.get(data);
  const locked = data.locked === true;
  if (previous) data.locked = applyHandleChange(construction, handles, previous, locked, spacing);
  else if (locked && construction.layout && hasHandles(data, [...construction.order])) construction.layout(handles, spacing);
  lastRendered.set(data, snapshotHandles(handles, construction.order));
}

function visibleSegments(spec: SegmentToolSpec, data: MeasurementData): Segment[] {
  // An owner whose line 2 is on another image shows only its own line here.
  const drawn = isCrossImageOwner(data) ? 1 : segmentsDrawn(data);
  return spec.segments.filter((s, i) => (spec.construction.segments === 2 ? i < drawn : true) && hasHandles(data, [s.from, s.to]));
}

/** Line 2 shown on its own image, labelled like the owner's, with the owner's measurement. */
function renderProxy(spec: SegmentToolSpec, ctx: CanvasRenderingContext2D, element: HTMLElement, proxy: MeasurementData, spacing: Spacing): void {
  const owner = proxyOwner(proxy);
  if (!owner) return;
  const color = csTools().toolColors.getColorIfActive(proxy);
  const { start, end, textBox } = proxy.handles;
  strokeLine(ctx, element, start, end, { color });
  const secondLabel = spec.segments[1]?.label;
  if (secondLabel) drawLabel(ctx, element, secondLabel, midpoint(start, end), color);
  csTools().drawHandles(ctx, { element }, { start, end }, color, { drawHandlesIfActive: false });
  proxy.readout = [];
  if (owner.complete === false) return;
  const result = spec.measure(handleMap(owner), spacing);
  if (result.canvas.length === 0) return;
  if (!textBox.hasMoved) placeTextBox(textBox, proxy);
  drawLinkedTextBox(ctx, element, textBox, result.canvas, [start, end], color);
}

const watchedElements = new WeakSet<HTMLElement>();

/** Moving either half of a cross-image pair redraws the other viewport (IMEXHS _femSynchronizer). */
function watchModifications(element: HTMLElement): void {
  if (watchedElements.has(element)) return;
  watchedElements.add(element);
  element.addEventListener(csTools().EVENTS.MEASUREMENT_MODIFIED, (event) => {
    const data = (event as CustomEvent<{ measurementData?: MeasurementData }>).detail?.measurementData;
    if (data && (isCrossProxy(data) || isCrossImageOwner(data))) updateAllViewports();
  });
}

function drawLabel(ctx: CanvasRenderingContext2D, element: HTMLElement, text: string, at: Point, color: string): void {
  const p = csCore().pixelToCanvas(element, at);
  drawTextBox(ctx, [text], p.x + LABEL_OFFSET_PX, p.y - LABEL_OFFSET_PX, color, { centerX: true, centerY: true });
}

function allHandlePoints(data: MeasurementData): Point[] {
  return Object.entries(data.handles).filter(([key]) => key !== 'textBox').map(([, h]) => h);
}

/** Default text box position: right of the rightmost handle. */
function placeTextBox(textBox: Handle, data: MeasurementData): void {
  const points = allHandlePoints(data);
  const right = points.reduce((best, p) => (p.x > best.x ? p : best), points[0]);
  textBox.x = right.x;
  textBox.y = right.y;
}

const line = (label: string, value: string): Readout => ({ label, value });
const ratioText = (value: number | undefined): string => (value === undefined ? '' : String(round(value, DECIMALS.ratio)));

// --- Bidireccional ------------------------------------------------------------------------------

const bidirectional: SegmentToolSpec = {
  name: 'Bidirectional',
  construction: BIDIRECTIONAL,
  segments: [{ from: 'start', to: 'end' }, { from: 'perpendicularStart', to: 'perpendicularEnd' }],
  measure(h, spacing) {
    const { length, width } = measureBidirectional(h, spacing);
    const l = formatLength(length, spacing, DECIMALS.bidirectional);
    const w = formatLength(width, spacing, DECIMALS.bidirectional);
    return { canvas: [`L: ${l}`, `W: ${w}`], readout: [line('Longitud:', `${l} x ${w}`)] };
  },
};

// --- Distancia entre líneas paralelas ----------------------------------------------------------

const parallelDistance: SegmentToolSpec = {
  name: 'ParallelDistance',
  construction: PARALLEL_DISTANCE,
  segments: [
    { from: 'start', to: 'end', dashed: true },
    { from: 'perpendicularStart', to: 'perpendicularEnd' },
    { from: 'perpendicularStart2', to: 'perpendicularEnd2' },
  ],
  measure(h, spacing) {
    const value = measureParallelDistance(h, spacing);
    if (value === undefined) return { canvas: [], readout: [] };
    const text = formatLength(value, spacing);
    return { canvas: [`Distancia: ${text}`], readout: [line('Distancia:', text)] };
  },
  anchors(h) {
    const feet = parallelFeet(h);
    return feet.length === 2 ? [midpoint(feet[0], feet[1])] : [h.start, h.end];
  },
};

// --- Índice cardiotorácico ---------------------------------------------------------------------

const cardiothoracicIndex: SegmentToolSpec = {
  name: 'CardiothoracicIndex',
  construction: CARDIOTHORACIC_INDEX,
  segments: [
    { from: 'start', to: 'end' },
    { from: 'perpendicularStart', to: 'perpendicularEnd', label: 'C', labelShift: { x: 0, y: CTI_LABEL_SHIFT_PX } },
    { from: 'leftStart', to: 'leftEnd', label: 'A', labelShift: { x: 0, y: -CTI_LABEL_SHIFT_PX } },
    { from: 'rightStart', to: 'rightEnd', label: 'B', labelShift: { x: 0, y: -CTI_LABEL_SHIFT_PX } },
  ],
  measure(h, spacing) {
    const { A, B, C, index } = measureCardiothoracic(h, spacing);
    const fmt = (v: number) => formatLength(v, spacing, DECIMALS.cardiothoracicLength);
    const canvas = [`A: ${fmt(A)}`, `B: ${fmt(B)}`, `C: ${fmt(C)}`];
    if (index === undefined) return { canvas, readout: [] };
    const text = index.toFixed(DECIMALS.index);
    canvas.push(`Índice: ${text}`);
    return { canvas, readout: [line('Índice:', text)] };
  },
  anchors: (h) => [h.perpendicularStart, h.perpendicularEnd],
};

// --- Coxometría -----------------------------------------------------------------------------------

const COXOMETRY_LABELS = ['H1', 'H2', 'D1', 'D2', 'D', 'I'] as const;

const coxometry: SegmentToolSpec = {
  name: 'Coxometry',
  construction: COXOMETRY,
  segments: [
    { from: 'start', to: 'end', dashed: true },
    { from: 'perpendicularStart', to: 'perpendicularEnd' },
    { from: 'perpendicularStart2', to: 'perpendicularEnd2' },
    { from: 'angleStart', to: 'angleEnd' },
    { from: 'angleStart2', to: 'angleEnd2' },
  ],
  labels(h) {
    const at = coxometryLabelPoints(h);
    return COXOMETRY_LABELS.flatMap((text) => (at[text] ? [{ text, at: at[text] }] : []));
  },
  measure(h, spacing) {
    const v = measureCoxometry(h, spacing);
    if (!v) return { canvas: [], readout: [] };
    const values: Array<[string, string]> = [
      ['D', formatAngle(v.D)],
      ['I', formatAngle(v.I)],
      ['D1', formatLength(v.D1, spacing)],
      ['D2', formatLength(v.D2, spacing)],
      ['H1', formatLength(v.H1, spacing)],
      ['H2', formatLength(v.H2, spacing)],
    ];
    return { canvas: values.map(([k, text]) => `${k}: ${text}`), readout: values.map(([k, text]) => line(`${k}:`, text)) };
  },
  anchors: (h) => [h.start, h.end],
};

// --- Insall-Salvati -----------------------------------------------------------------------------

const insallSalvati: SegmentToolSpec = {
  name: 'InsallSalvati',
  construction: TWO_LINES,
  segments: [
    { from: 'start', to: 'end', label: 'A' },
    { from: 'start2', to: 'end2', label: 'B' },
  ],
  measure(h, spacing) {
    const { A, B, ratioAB, ratioBA } = measureInsallSalvati(h, spacing);
    const ab = ratioText(ratioAB);
    const canvas = [`Relación AB: ${ab}`, `A: ${formatLength(A, spacing)}`, `B: ${formatLength(B, spacing)}`, `Relación BA: ${ratioText(ratioBA)}`];
    return { canvas, readout: ratioAB === undefined ? [] : [line('Relación AB:', ab)] };
  },
};

// --- Anteversión femoral ------------------------------------------------------------------------

const femoralAnteversion: SegmentToolSpec = {
  name: 'FemoralAnteversion',
  construction: TWO_LINES,
  segments: [
    { from: 'start', to: 'end', label: 'A' },
    { from: 'start2', to: 'end2', label: 'B' },
  ],
  crossViewport: true,
  measure(h, spacing) {
    const angle = formatAngle(measureTwoLineAngle(h, spacing));
    return { canvas: [`Ángulo AB: ${angle}`], readout: [line('Angulo:', angle)] };
  },
};

/** IMEXHS tool id → segment tool spec. */
export const SEGMENT_TOOLS = {
  Bidirectional: bidirectional,
  cardiothoracicIndex,
  parallelDistance,
  salvati: insallSalvati,
  coxometry,
  cobbAngleMV: femoralAnteversion,
} as const;
